export class Descriptive {

    static mean(data: number[]): number {
        return Descriptive.sum(data) / data.length;
    }

    static median(data: number[]): number {
        const sortedDataset = data.sort((left, right) => left - right);
        const middleIndex = sortedDataset.length / 2;

        if (!this.isEven(data.length)) {
            const index = Math.floor(middleIndex);
            return sortedDataset[index];
        }
        const lowerIndex = middleIndex - 1;
        return (sortedDataset[lowerIndex] + sortedDataset[middleIndex]) / 2;

    }

    static max(data: number[]): number {
        const nmax = data.reduce((left, right) => left > right ? left : right, -Infinity);
        return nmax;
    }

    static min(data: number[]): number {
        const nmin = data.reduce((left, right) => left < right ? left : right, Infinity);
        return nmin;
    }

    static mode(data: number[]): number[] {
        const table = Descriptive.table(data);
        const maximum = Descriptive.max(data);
        const isMax = key => table[key] === maximum;
        const toNum = key => Number(key);

        return Object
            .keys(table)
            .filter(isMax)
            .map(toNum);
    }

    static variance(data: number[]): number {
        const dataMean = Descriptive.mean(data);
        const squared = val => Math.pow(val, 2);
        const calcVariance = val => squared(val - dataMean);
        const variances = data.map(calcVariance);
        return this.sum(variances) / data.length;
    }

    static stdDev(data: number[]): number {
        const populationVariance = Descriptive.variance(data);
        return Math.sqrt(populationVariance);
    }

    static sum(data: number[]) {
        const nsum = data.reduce((left, right) => left + right, 0);
        return nsum;
    }

    static isEven(value: number): boolean {
        return value % 2 === 0;
    }

    static table(data: number[]): Dataset {
        const addFreq = (freqs, val) => { freqs[val] = (freqs[val] || 0) + 1; return freqs; };
        return data.reduce(addFreq, {});
    }

    static roundTo(n, decimals): number {
        let negative = false;
        if (decimals === undefined) {
            decimals = 0;
        }
        if (n < 0) {
            negative = true;
            n = n * -1;
        }
        const multiplicator = Math.pow(10, decimals);
        n = parseFloat((n * multiplicator).toFixed(11));
        let nString = (Math.round(n) / multiplicator).toFixed(decimals);
        if (negative) {
            nString = (n * -1).toFixed(decimals);
        }
        return parseFloat(nString);
    }
}

export interface Dataset {
    [index: string]: number;
}
